/**
 * Shared Help page component with game-specific content.
 * Displays how-to-play instructions and game tutorial.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../Store/store';
import { ButtonStylized, ButtonStylizedType } from '../../ButtonStylized/ButtonStylized';
import { BasePageProps } from '../../Page/PageTypes';
import * as Lucide from 'lucide-react';

/**
 * Props for the PageHelp component.
 */
interface PageHelpProps extends BasePageProps {
  // Additional props can be added here for customization.
  // TODO: Add support for different help content types when adding new games.
  onNavigate?: (pageId: string) => void;
}

/**
 * Help page component showing game instructions.
 * 
 * This component displays:
 * - Game tutorial GIF/video
 * - Written instructions
 * - Play button to dismiss and start playing
 * 
 * Content is automatically pulled from the game configuration via Redux,
 * making this component reusable across different games.
 */
export const PageHelp: React.FC<PageHelpProps> = ({
  onNavigate,
  onRequestClose
}) => {
  // Get game data from Redux
  const { currentGame: gameSettings } = useSelector((state: RootState) => state.gameSession);
  
  const handlePlayClick = () => {
    if (onRequestClose) {
      onRequestClose();
    }
  };

  // Early return if no game data
  if (!gameSettings) {
    return <div>Loading...</div>;
  };

  return (
    <div className="page-help" style={{width: '100%'}}>
      <div style={{ paddingTop: '20px', textAlign: 'center', width: '100%' }}>
        {gameSettings?.howToPlayGif ? (
          <img 
            src={gameSettings.howToPlayGif} 
            alt="How to play" 
            style={{
              aspectRatio: 'auto',
              maxHeight: '80%',
              maxWidth: '300px',
              width: '90%'
            }} 
          />
        ) : (
          <div style={{fontSize: "1.1rem", width: "90%", margin: "0 auto"}}>
            {gameSettings.howToPlayText ? gameSettings.howToPlayText : "No help information available for this game."}
          </div>
        )}
        
        {/* Play button to dismiss help and start playing. */}
        <ButtonStylized 
          buttonType={ButtonStylizedType.Primary}
          onClick={handlePlayClick}
          icon={<Lucide.Check size={26} strokeWidth={3} />}
          text="Got it!"
          additionalStyles={{ 
            fontSize: '1.3rem', 
            marginTop: '20px',
            marginBottom: '20px',
            backgroundColor: gameSettings.colorPrimary
          }}
        />
      </div>
    </div>
  );
};
